// What the template screens share. Nothing here fetches.
//
// The model underneath (4.2): a template has versions; every save is a new
// version and a published one never changes. A version is one MJML source
// and a list of variants — one per language, optionally narrowed to one
// country — each with its subject, preheader and the `t.*` texts the source
// reads as `{{ t.name }}`. That is how one layout serves nine countries.

import type { SendingIdentity, TemplateOverviewRow, TemplateStatus, TemplateVariant } from "@/lib/api";

/** The width MJML lays a letter out at: the desktop preview's frame. */
export const EMAIL_WIDTH = 600;
/** A narrow phone: the mobile preview's frame. */
export const PHONE_WIDTH = 375;

export function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}

// --- keys -----------------------------------------------------------------------

/** The API's own shape for a template key (service.KEY_SHAPE). */
export const KEY_SHAPE = /^[a-z0-9][a-z0-9._-]{1,79}$/;

/** "black-friday-2026-hu" from "Black Friday 2026 – HU": accents dropped,
 * everything else a dash. Empty when nothing usable is left. */
export function keyFromName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^[-._]+|[-._]+$/g, "")
    .slice(0, 80);
}

const COPY_NUMBER = /^(.+)-(\d{1,3})$/;

/** The key "Másolat készítése" proposes: the original's with the first free
 * number, `mobilfox-alap` → `mobilfox-alap-2`. A copy of a copy numbers on
 * from the original (`mobilfox-alap-2` → `-3`, not `-2-2`) — but only when
 * the original is there, so the year in `black-friday-2026` stays. The
 * source's own key counts as taken whatever `taken` says. */
export function copyKey(key: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  used.add(key);
  const numbered = COPY_NUMBER.exec(key);
  const stem = numbered !== null && used.has(numbered[1]!) ? numbered[1]! : key;
  for (let number = 2; ; number += 1) {
    const suffix = `-${number}`;
    const candidate = `${stem.slice(0, 80 - suffix.length).replace(/[-._]+$/, "")}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
}

/** "MobilFox alap (másolat)", within the 160 characters a name may have. */
export function copyName(name: string): string {
  const suffix = " (másolat)";
  return `${name.trim().slice(0, 160 - suffix.length).trimEnd()}${suffix}`;
}

/** What "Másolat készítése" copies: a template and its newest version. */
export interface CopySource {
  id: number;
  name: string;
  key: string;
  /** The newest version — the one that is copied. */
  version: number | null;
  /** The newest version's default language: the copy's, unless changed. */
  defaultLanguage: string;
  /** The languages the newest version has wording in; the base is one of them. */
  languages: string[];
  /** Every key there is, archived ones too, for a free one to propose. */
  takenKeys: string[];
  /** A word above the fields, e.g. that the version on screen is not the newest. */
  note?: string;
}

/** A gallery card as what "Másolat készítése" copies; `rows` is the whole
 * gallery, archived cards too, whose keys are taken. */
export function copySourceOf(row: TemplateOverviewRow, rows: TemplateOverviewRow[]): CopySource {
  return {
    id: row.id,
    name: row.name,
    key: row.key,
    version: row.latest?.version ?? null,
    defaultLanguage: row.latest?.default_language ?? "hu",
    languages: row.languages.map((entry) => entry.language),
    takenKeys: rows.map((entry) => entry.key),
  };
}

/** A `t.*` name the template can read with a dot: a Jinja identifier. */
export const STRING_KEY_SHAPE = /^[a-z_][a-z0-9_]{0,39}$/;

export function stringKeyFrom(label: string): string {
  return label
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^(\d)/, "_$1")
    .slice(0, 40);
}

// --- languages and countries -------------------------------------------------------

export const LANGUAGE_NAMES: Record<string, string> = {
  hu: "magyar",
  ro: "román",
  pl: "lengyel",
  el: "görög",
  sk: "szlovák",
  bg: "bolgár",
  it: "olasz",
  cs: "cseh",
  en: "angol",
  de: "német",
};

/** "magyar (hu)"; an unknown code is shown as itself. */
export function languageName(language: string): string {
  const name = LANGUAGE_NAMES[language.toLowerCase()];
  return name ? `${name} (${language})` : language;
}

/** A variant's tab: "hu", or "it · IT" when it serves one country only. */
export function variantLabel(variant: { language: string; country: string }): string {
  return variant.country ? `${variant.language} · ${variant.country}` : variant.language;
}

function base(language: string): string {
  return language.toLowerCase().split(/[-_]/)[0] ?? "";
}

/** The country whose sending account renders a variant: its sender, its
 * footer. A variant narrowed to a country is that country's; otherwise the
 * account that speaks the language. Null when no account speaks it. */
export function countryOf(
  identities: SendingIdentity[],
  variant: { language: string; country: string },
): string | null {
  if (variant.country) return variant.country;
  const wanted = variant.language.toLowerCase();
  const exact = identities.find((row) => row.language.toLowerCase() === wanted);
  if (exact) return exact.country;
  return identities.find((row) => base(row.language) === base(wanted))?.country ?? null;
}

/** The country whose account renders a variant for a picture or a check: the
 * one its send would use (`countryOf`), else any active account. A picture of
 * the letter does not depend on which account speaks its language, and neither
 * does the send: `select_variant` falls back to the version's default language,
 * and publishing compiles a variant no account speaks under the first active
 * account (`service._account_for`) — the same first one as here, both in
 * country order. Null only when there is no active account at all. */
export function renderCountryOf(
  identities: SendingIdentity[],
  variant: { language: string; country: string },
): string | null {
  return countryOf(identities, variant) ?? identities[0]?.country ?? null;
}

/** Where a variant goes, as the send decides it (`select_variant`):
 * - `own` — an account sends in its language, or it is narrowed to a country;
 * - `default` — no account speaks it, but it is the template's default
 *   language, so every country without its own-language variant gets it;
 * - `contacts` — no account speaks it and it is not the default: the contacts
 *   whose own language it is get it, in any country;
 * - `nowhere` — no active account, so nothing goes anywhere.
 * `country` is the account it is rendered and checked with (`renderCountryOf`). */
export type LanguageReach =
  | { kind: "own" | "default" | "contacts"; country: string }
  | { kind: "nowhere" };

export function languageReach(
  identities: SendingIdentity[],
  variant: { language: string; country: string },
  defaultLanguage: string,
): LanguageReach {
  const country = renderCountryOf(identities, variant);
  if (country === null) return { kind: "nowhere" };
  if (countryOf(identities, variant) !== null) return { kind: "own", country };
  const isDefault = !variant.country && defaultLanguage !== "" && base(variant.language) === base(defaultLanguage);
  return { kind: isDefault ? "default" : "contacts", country };
}

/** One sentence on what the send does with a language no account speaks; null
 * when an account speaks it and there is nothing to say. Never "would not be
 * sent" unless that is true — no active account at all. */
export function reachNote(reach: LanguageReach, language: string): string | null {
  switch (reach.kind) {
    case "own":
      return null;
    case "default":
      return `Egyik fiók sem ${language} nyelven küld, de ez az alapnyelv: minden országba ez megy, ahol nincs saját nyelvű változat. A ${reach.country} fiók láblécével ellenőrizve.`;
    case "contacts":
      return `Egyik fiók sem ${language} nyelven küld: ezt a változatot a(z) ${language} nyelvű kontaktok kapják, bármelyik országban. A ${reach.country} fiók láblécével ellenőrizve.`;
    case "nowhere":
      return "Nincs aktív küldő-fiók, így nincs feladó és lábléc: a levél sehová nem menne ki.";
  }
}

/** The languages the sending accounts speak, each once, in account order. */
export function accountLanguages(identities: SendingIdentity[]): string[] {
  return [...new Set(identities.map((row) => row.language.toLowerCase()))];
}

// --- the status a gallery card shows ----------------------------------------------

/** Archived wins; otherwise the newest version's own state. */
export function cardStatus(row: { is_archived: boolean; latest: { status: TemplateStatus } | null }): TemplateStatus {
  if (row.is_archived) return "archived";
  return row.latest?.status ?? "draft";
}

// --- variants being edited --------------------------------------------------------

/** A variant as the editor holds it: the wire shape without the row id. */
export interface DraftVariant {
  language: string;
  country: string;
  subject: string;
  preheader: string;
  strings: Record<string, string>;
}

export function draftOf(variant: TemplateVariant): DraftVariant {
  const strings: Record<string, string> = {};
  for (const [name, value] of Object.entries(variant.strings ?? {})) strings[name] = String(value ?? "");
  return {
    language: variant.language,
    country: variant.country,
    subject: variant.subject,
    preheader: variant.preheader,
    strings,
  };
}

export function sameVariants(a: DraftVariant[], b: DraftVariant[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** The `t.*` names these texts read: `{{ t.cta }}`, `{{ t.cta | upper }}`,
 * `{% if t.promo %}`, `t["cta"]`. Only inside a tag — a sentence that says
 * "t.cta" is not a read. */
export function stringNames(...texts: string[]): string[] {
  const names = new Set<string>();
  const tags = /\{\{([\s\S]*?)\}\}|\{%([\s\S]*?)%\}/g;
  const dotted = /(?<![\w.])t\.([A-Za-z_]\w*)/g;
  const indexed = /(?<![\w.])t\[\s*["']([^"'\]]+)["']\s*\]/g;
  for (const text of texts) {
    for (const tag of text.matchAll(tags)) {
      const inner = tag[1] ?? tag[2] ?? "";
      for (const hit of inner.matchAll(dotted)) if (hit[1]) names.add(hit[1]);
      for (const hit of inner.matchAll(indexed)) if (hit[1]) names.add(hit[1]);
    }
  }
  return [...names].sort();
}

export interface Completeness {
  /** Subject and preheader: the API will not store a variant without them. */
  empty: ("subject" | "preheader")[];
  /** `t.*` names the letter reads that this variant has no text for — they
   * render as nothing, silently. */
  missing: string[];
  /** Word for word the default language's subject: probably not translated. */
  sameAsDefault: boolean;
  /** `t.*` texts with a template tag in them. The engine hands a text to the
   * letter as a value, not as a template: `{{ first_name }}` inside one
   * reaches the recipient with its braces. */
  tagged: string[];
}

// `{{ … }}`, `{% … %}`, `{# … #}`: what the engine would read as a tag.
const TAG_IN_TEXT = /\{\{|\{%|\{#/;

export function completenessOf(
  variant: DraftVariant,
  used: string[],
  reference: DraftVariant | null,
): Completeness {
  const empty: Completeness["empty"] = [];
  if (!variant.subject.trim()) empty.push("subject");
  if (!variant.preheader.trim()) empty.push("preheader");
  const missing = used.filter((name) => !(variant.strings[name] ?? "").trim());
  const sameAsDefault =
    reference !== null &&
    reference !== variant &&
    base(reference.language) !== base(variant.language) &&
    variant.subject.trim() !== "" &&
    variant.subject.trim() === reference.subject.trim();
  const tagged = Object.keys(variant.strings)
    .filter((name) => TAG_IN_TEXT.test(variant.strings[name] ?? ""))
    .sort();
  return { empty, missing, sameAsDefault, tagged };
}

export function isComplete(check: Completeness): boolean {
  return check.empty.length === 0 && check.missing.length === 0 && check.tagged.length === 0;
}

// --- merge tags ---------------------------------------------------------------------

/** How the engine spells a read of `name`. */
export function mergeTag(name: string): string {
  return `{{ ${name} }}`;
}
